import {
  Document,
  Page,
  Text,
  View,
  Font,
  StyleSheet,
  Svg,
  Rect,
  Line,
} from '@react-pdf/renderer'
import { IMerchant } from '../../../types/merchant'
import { EOrderType, IOrder, IOrderItem } from '../../../types/order'
import SarabunFont from '../../../assets/fonts/th-sarabun-new.ttf'
import SarabunFontBold from '../../../assets/fonts/th-sarabun-bold.ttf'
import dayjs from 'dayjs'
import { numberFormat } from '../../../utils'
import { VAT_TYPES } from '../../../constants/global'

Font.register({ family: 'Sarabun', src: SarabunFont })
Font.register({ family: 'Sarabun', src: SarabunFontBold, fontWeight: 'bold' })

interface Props {
  orders: IOrder[]

  merchant: any | IMerchant
  vatType?: VAT_TYPES
  size?: 'A4' | '80mm'
  showDate?: string
  isCustomPayment?: boolean
  showProductInPackaging?: boolean
}

const styles = StyleSheet.create({
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginBottom: 5,
    overflow: 'hidden',
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#000',
    marginRight: 6,
  },
  label: {
    fontSize: 12,
  },
})

const Checkbox = ({ size = 12 }) => (
  <Svg height={size} width={size} style={{ marginRight: 6 }}>
    <Rect
      x={0}
      y={0}
      width={size}
      height={size}
      stroke="#000"
      strokeWidth={1}
      fill="none"
    />
  </Svg>
)
const Underline = ({ width = 200 }) => (
  <Svg height="1" width={width}>
    <Line x1="0" y1="0" x2={width} y2="0" stroke="#000" strokeWidth={0.5} />
  </Svg>
)

const sizes = {
  A4: {
    width: 8.27,
    height: 11.69,
  },
  '80mm': {
    width: 3.15,
    height: 11.69,
  },
}

const PDFReceipt = (props: Props) => {
  const {
    orders,
    merchant,
    vatType = VAT_TYPES.NON_VAT,
    size = 'A4',
    showDate = 'order',
    isCustomPayment = false,
    showProductInPackaging = false,
  } = props
  const fontSize = size === 'A4' ? 14 : 10
  const paperPadding = size === 'A4' ? 20 : 8
  const vat = 0.07
  const paperSize = sizes[size] || sizes.A4

  const itemValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value / 1.07
      case 'exclude':
        return value
      default:
        return value
    }
  }

  const vatValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value - value / 1.07
      case 'exclude':
        return value * vat
      default:
        return 0
    }
  }

  const subTotalValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value / 1.07
      case 'exclude':
        return value
      default:
        return value
    }
  }

  const totalValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value
      case 'exclude':
        return value * vat + value
      default:
        return value
    }
  }

  return (
    <Document>
      {orders.map((order: IOrder, index: number) => {
        return (
          <Page
            size={[paperSize.width * 72, paperSize.height * 72]}
            key={index}
            style={{
              flexDirection: 'row',
              fontFamily: 'Sarabun',
            }}
          >
            <View
              style={{
                flex: 1,
                padding: paperPadding,
                fontSize,
                lineHeight: 1,
              }}
            >
              <Text style={{ textAlign: 'left' }}>
                <Text style={{ fontWeight: 'bold' }}>ชื่อบริษัท:</Text>{' '}
                {merchant?.name}
              </Text>
              <Text style={{ textAlign: 'left' }}>
                <Text style={{ fontWeight: 'bold' }}>ที่อยู่:</Text>{' '}
                {merchant?.address}
              </Text>
              <View
                style={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  marginBottom: 15,
                }}
              >
                <Text style={{ textAlign: 'left', width: 200 }}>
                  <Text style={{ fontWeight: 'bold' }}>โทร:</Text>{' '}
                  {merchant?.phoneNumber}
                </Text>
                <Text style={{ fontSize, textAlign: 'left' }}>
                  <Text style={{ fontWeight: 'bold' }}>
                    เลขประจำผู้เสียภาษี:
                  </Text>{' '}
                  {merchant?.taxIdentificationNumber}
                </Text>
              </View>
              <View
                style={{
                  alignItems: 'center',
                  marginBottom: 5,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                <Text style={{ textAlign: 'center', width: '100%' }}>
                  ใบกำกับภาษี/ ใบเสร็จรับเงิน
                </Text>
                <Text style={{ textAlign: 'center' }}>
                  RECEIPT/TAX INVOICE (ORIGINAL)
                </Text>
              </View>
              <Text style={{ textAlign: 'right', fontWeight: 300 }}>
                เลขที่ NO: {order?.referrer?.refId || order?.code}
              </Text>
              <Text
                style={{
                  textAlign: 'right',
                  fontWeight: 300,
                  marginBottom: 15,
                }}
              >
                วันที่ DATE:{' '}
                {dayjs(
                  showDate === 'order' ? order?.orderDate : new Date(),
                ).format('DD/MM/YYYY')}
              </Text>
              <View
                style={{
                  flexDirection: 'column',
                  marginBottom: 10,
                }}
              >
                <Text style={{ textAlign: 'left' }}>
                  <Text style={{ fontWeight: 'bold' }}>ชื่อ/ Customer:</Text>{' '}
                  {order?.recipient.name ?? '-'}
                </Text>
                <Text style={{ textAlign: 'left' }}>
                  <Text style={{ fontWeight: 'bold' }}>ที่อยู่/ Address:</Text>{' '}
                  {order?.recipient.address ?? '-'} ตำบล/แขวง{' '}
                  {order?.recipient.subDistrict ?? '-'} อำเภอ/เขต{' '}
                  {order?.recipient.district ?? '-'} จังหวัด{' '}
                  {order?.recipient.province ?? '-'}{' '}
                  {order?.recipient.zipcode ?? ''}
                </Text>
              </View>
              <View
                style={{
                  flexDirection: 'column',
                  marginBottom: 10,
                }}
              >
                <Text style={{ textAlign: 'left' }}>
                  <Text style={{ fontWeight: 'bold' }}>
                    เลขผู้เสียภาษี/ TAX ID:
                  </Text>{' '}
                  {order?.recipient.crn ?? '-'}
                </Text>
                <Text style={{ textAlign: 'left' }}>
                  <Text style={{ fontWeight: 'bold' }}>โทร./ Phone:</Text>{' '}
                  {order?.recipient.phone ?? '-'}
                </Text>
              </View>
              <View
                style={{
                  flexDirection: 'column',
                  fontSize: 13,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    fontWeight: 'bold',
                  }}
                >
                  <Text
                    style={{
                      width: '15%',
                      ...TableStyle.lt,
                      ...TableStyle.header,
                    }}
                  >
                    รหัสสินค้า
                  </Text>
                  <Text
                    style={{
                      width: '40%',
                      ...TableStyle.lt,
                      ...TableStyle.header,
                    }}
                  >
                    รายการ
                  </Text>
                  <Text
                    style={{
                      width: '15%',
                      ...TableStyle.lt,
                      ...TableStyle.header,
                    }}
                  >
                    จำนวน
                  </Text>
                  <Text
                    style={{
                      width: '15%',
                      ...TableStyle.lt,
                      ...TableStyle.header,
                    }}
                  >
                    ราคาต่อหน่วย
                  </Text>
                  <Text
                    style={{
                      width: '15%',
                      ...TableStyle.lrt,
                      ...TableStyle.header,
                    }}
                  >
                    จำนวนเงิน
                  </Text>
                </View>
                {order.items.map((item: IOrderItem, itemIndex: number) => (
                  <View
                    key={`item-${itemIndex}`}
                    style={{
                      flexDirection: 'row',
                      ...TableStyle.b,
                    }}
                  >
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      {item.productCode}
                    </Text>
                    <View
                      style={{
                        width: '40%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Text style={{ fontWeight: 'bold' }}>
                        {item.productName}
                      </Text>
                      {!!item.productDescription && (
                        <Text>{item.productDescription}</Text>
                      )}
                      {showProductInPackaging && item.isCollection && (
                        <Text style={{ fontSize: 11 }}>
                          {item.collections
                            ?.map(
                              (collection) =>
                                `${collection.productName} x ${collection.quantity}`,
                            )
                            .join(', ')}
                        </Text>
                      )}
                    </View>
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      {item.quantity}
                    </Text>
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      {numberFormat(item.price, 2)}
                    </Text>
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lrt,
                        ...TableStyle.header,
                      }}
                    >
                      {numberFormat(itemValue(item.total), 2)}
                    </Text>
                  </View>
                ))}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'stretch',
                  }}
                >
                  <Text
                    style={{
                      width: '40%',
                      ...TableStyle.lrb,
                      ...TableStyle.header,
                      textAlign: 'left',
                    }}
                  >
                    หมายเหตุ: {order.note ?? '-'}
                  </Text>
                  <View
                    style={{
                      flexDirection: 'column',
                      width: '60%',
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                      }}
                    >
                      <Text
                        style={{
                          width: '75%',
                          ...TableStyle.b,
                          ...TableStyle.footer,
                          textAlign: 'right',
                          fontWeight: 'bold',
                        }}
                      >
                        ราคารวม
                      </Text>
                      <Text
                        style={{
                          width: '25%',
                          ...TableStyle.lrb,
                          ...TableStyle.header,
                          fontWeight: 'bold',
                        }}
                      >
                        {numberFormat(subTotalValue(order.total), 2)}
                      </Text>
                    </View>
                    {vatType !== VAT_TYPES.NON_VAT && (
                      <View
                        style={{
                          flexDirection: 'row',
                        }}
                      >
                        <Text
                          style={{
                            width: '75%',
                            ...TableStyle.b,
                            ...TableStyle.footer,
                            textAlign: 'right',
                            fontWeight: 'bold',
                          }}
                        >
                          VAT 7%
                        </Text>
                        <Text
                          style={{
                            width: '25%',
                            ...TableStyle.lrb,
                            ...TableStyle.header,
                            fontWeight: 'bold',
                          }}
                        >
                          {numberFormat(vatValue(order.total), 2)}
                        </Text>
                      </View>
                    )}
                    <View
                      style={{
                        flexDirection: 'row',
                      }}
                    >
                      <Text
                        style={{
                          width: '75%',
                          ...TableStyle.b,
                          ...TableStyle.footer,
                          textAlign: 'right',
                          fontWeight: 'bold',
                        }}
                      >
                        ยอดรวมทั้งสิ้น
                      </Text>
                      <Text
                        style={{
                          width: '25%',
                          ...TableStyle.lrb,
                          ...TableStyle.header,
                          fontWeight: 'bold',
                        }}
                      >
                        {numberFormat(totalValue(order.total), 2)}
                      </Text>
                    </View>
                  </View>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginTop: 20,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'column',
                      width: '60%',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                    }}
                  >
                    {isCustomPayment ? (
                      <>
                        <Text style={{ textAlign: 'left', marginBottom: 5 }}>
                          ได้รับเงินแล้ว
                        </Text>
                        <View style={{ marginBottom: 10 }}>
                          <View style={styles.checkboxContainer}>
                            <Checkbox />
                            <Text style={styles.label}>เงินสด (Cash)</Text>
                          </View>
                          <View style={styles.checkboxContainer}>
                            <Checkbox />
                            <Text style={styles.label}>
                              เช็คธนาคาร (Cheque
                              Bank).........................................................................................................
                            </Text>
                          </View>
                          <View style={styles.checkboxContainer}>
                            <Text style={styles.label}>
                              เลขที่.......................................................................ลงวันที่.......................................................................
                            </Text>
                          </View>
                          <View style={styles.checkboxContainer}>
                            <Text style={styles.label}>
                              สาขา.........................................................................................................................................................
                            </Text>
                          </View>
                          <View style={styles.checkboxContainer}>
                            <Checkbox />
                            <Text style={styles.label}>
                              โอนเงินเข้าบัญชี...............................ธนาคาร.....................................สาขา......................................
                            </Text>
                          </View>
                          <View style={styles.checkboxContainer}>
                            <Text style={styles.label}>
                              ชื่อบัญชี..............................................เลขบัญชี.............................................เลขที่....................................
                            </Text>
                          </View>
                        </View>
                        <View style={styles.checkboxContainer}>
                          <Text style={styles.label}>
                            ผู้รับเงิน.....................................................................................วันที่..........................................................
                          </Text>
                        </View>
                      </>
                    ) : (
                      <>
                        <Text style={{ textAlign: 'left' }}>
                          <Text style={{ fontWeight: 'bold' }}>
                            รูปแบบการชำระเงิน:
                          </Text>{' '}
                          {order.paymentMethod === EOrderType.COD
                            ? 'ชำระเงินปลายทาง'
                            : 'โอนผ่านธนาคาร'}
                        </Text>
                        {order.paymentMethod === EOrderType.BANK &&
                          !!order.paymentDate && (
                            <Text style={{ textAlign: 'left' }}>
                              <Text style={{ fontWeight: 'bold' }}>
                                ชำระเมื่อ:
                              </Text>{' '}
                              {dayjs(order.paymentDate).format('DD/MM/YYYY')}
                            </Text>
                          )}
                        {!!order.trackingCode && (
                          <Text style={{ textAlign: 'left' }}>
                            <Text style={{ fontWeight: 'bold' }}>
                              หมายเลขพัสดุ:
                            </Text>{' '}
                            {order.trackingCode}
                          </Text>
                        )}
                      </>
                    )}
                  </View>
                  <View
                    style={{
                      flexDirection: 'column',
                      width: '20%',
                    }}
                  >
                    <Text
                      style={{
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      ผู้อนุมัติ
                    </Text>
                    <Text
                      style={{
                        width: '100%',
                        textAlign: 'center',
                        paddingTop: 20,
                        marginBottom: 10,
                        borderBottom: '1px dotted #000',
                      }}
                    ></Text>
                    <Text
                      style={{
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      วันที่...............................................
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </Page>
        )
      })}
    </Document>
  )
}

export default PDFReceipt

const borderColor = '#333'

const TableStyle = StyleSheet.create({
  header: {
    padding: 5,
    textAlign: 'center',
  },
  footer: {
    padding: 5,
    textAlign: 'right',
    backgroundColor: '#f0f0f0',
  },
  t: {
    borderTopWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  tb: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lt: {
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  rt: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lrt: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  b: {
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lb: {
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  rb: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lrb: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
})
